import { useGiftItem } from '@hooks/businessContainer';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import logger from '@utils/logger';
import { ISynchronizeCart } from '@monofront/business/hooks';
import { format } from '@monofront/business/utils';
import { trackEvent } from '@monofront/tracking';
import useMinimumOrderAmount from '@hooks/useMinimumOrderAmount';
import { useCheckoutCartId } from '@monofront/business/contexts';

interface IProceedCheckoutReturn {
  proceedCheckout: () => Promise<ISynchronizeCart | void>;
  minimumOrderAmountNotReached: () => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

export const useProceedCheckout = (): IProceedCheckoutReturn => {
  const { giftItem, giftItemInShoppingCart, removeGiftItemFromShoppingCart } =
    useGiftItem();
  const router = useRouter();
  const [, setCheckoutCartId] = useCheckoutCartId();
  const [isLoading, setIsLoading] = useState(false);
  const { shoppingCart } = useShoppingCart();
  const { minimumOrderAmount } = useMinimumOrderAmount();

  const t = useTranslations('Cart');
  const et = useTranslations('Errors');

  const minimumOrderAmountNotReached = () => {
    trackEvent({
      type: 'LFMinimumOrderAmountNotReached',
      data: {
        cartValue: shoppingCart.totalPrice,
      },
    });

    toast(
      et.rich('minimumOrderAmountNotReached', {
        missingAmount: format(minimumOrderAmount - shoppingCart.totalPrice),
      }),
      {
        progressClassName: 'toast-error',
      },
    );
  };

  const proceedCheckout = async () => {
    setCheckoutCartId(undefined);

    if (!shoppingCart.products?.length) {
      toast(t('emptyCart'), {
        progressClassName: 'toast-error',
      });

      return;
    }

    if (shoppingCart.totalPrice < minimumOrderAmount) {
      minimumOrderAmountNotReached();

      return;
    }

    if (giftItemInShoppingCart && giftItemInShoppingCart.id !== giftItem?.id) {
      logger.warn(
        'Current shopping cart gift item not matched current eligible gift item',
        {
          shopping_cart_item: giftItemInShoppingCart.id,
          current_eligible_gift_item: giftItem?.id,
        },
      );

      await removeGiftItemFromShoppingCart();

      const message = giftItem
        ? t('giftItemUpdated')
        : `${et('anErrorOccured')} ${et('tryAgain')}`;

      toast(message);

      return;
    }

    setIsLoading(true);

    router.push('/checkout/delivery');
  };

  return {
    proceedCheckout,
    minimumOrderAmountNotReached,
    isLoading,
    setIsLoading,
  };
};
